import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Filter, Eye, Calendar, User, Folder, Ticket, AlertCircle, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api` || 'http://localhost:8001/api';

const UnifiedTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [teams, setTeams] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [itemsPerPage] = useState(50);
  
  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(null);

  // Helper function to truncate text with hover tooltip
  const TruncatedText = ({ text, maxLength = 15, className = "" }) => {
    if (!text) return <span className={className}>-</span>;
    
    const shouldTruncate = text.length > maxLength;
    const displayText = shouldTruncate ? `${text.substring(0, maxLength)}...` : text;
    
    return (
      <span 
        className={`${className} ${shouldTruncate ? 'cursor-help' : ''}`}
        title={shouldTruncate ? text : undefined}
      >
        {displayText}
      </span>
    );
  };

  useEffect(() => {
    // Get current user from localStorage
    const userData = localStorage.getItem('bizmanager_user');
    if (userData) {
      const user = JSON.parse(userData);
      setCurrentUser(user);
      
      // If user is an employee, auto-set team filter to their team
      if (user.isEmployee && user.team_type_id) {
        setTeamFilter(user.team_type_id);
      }
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchTasks();
      fetchTeams();
    }
  }, [typeFilter, statusFilter, teamFilter, currentUser, currentPage]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [typeFilter, statusFilter, teamFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.append('task_type', typeFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (teamFilter) params.append('team_id', teamFilter);
      
      // Add pagination parameters
      const skip = (currentPage - 1) * itemsPerPage;
      params.append('skip', skip.toString());
      params.append('limit', itemsPerPage.toString());
      
      const response = await axios.get(`${API}/tasks/unified?${params.toString()}`);
      setTasks(response.data);
      
      // For now, we'll get total count by making a separate request without pagination
      // In a real app, the API should return total count with paginated results
      const totalResponse = await axios.get(`${API}/tasks/unified?${new URLSearchParams({
        ...(typeFilter && { task_type: typeFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(teamFilter && { team_id: teamFilter }),
        limit: '1000' // Get a large number to count total
      }).toString()}`);
      setTotalTasks(totalResponse.data.length);
    } catch (error) {
      console.error('Error fetching unified tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/team-types`);
      setTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case 'Project':
        return <Folder className="h-4 w-4 text-blue-500" />;
      case 'Support':
        return <Ticket className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getTaskTypeBadgeClass = (type) => {
    switch (type) {
      case 'Project':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Support':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Open':
      case 'To Do':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'In Progress':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'In testing':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'Resolved':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Published (Prod)':
        return <CheckCircle className="h-4 w-4 text-purple-500" />;
      case 'Closed':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Open':
      case 'To Do':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'In testing':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Published (Prod)':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleTaskView = (task) => {
    if (task.type === 'Support') {
      navigate(`/support/tickets/${task.ticket_id}`);
    } else if (task.type === 'Project') {
      // Navigate to project task view - we'll create this later
      navigate(`/projects/${task.project_id}/tasks/${task.task_index}`);
    }
  };

  const filteredTasks = tasks.filter(task =>
    task.task_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (task.team_name && task.team_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.assigned_employee_name && task.assigned_employee_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.project_name && task.project_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (task.client_name && task.client_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">All Tasks</h1>
          <p className="text-gray-600 mt-1">Unified view of project tasks and support tickets</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="Project">Project</option>
              <option value="Support">Support</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="To Do">To Do</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="In testing">In testing</option>
              <option value="Resolved">Resolved</option>
              <option value="Published (Prod)">Published (Prod)</option>
              <option value="Closed">Closed</option>
            </select>

            <select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Teams</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tasks Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading tasks...</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-500">
              {searchTerm || typeFilter || statusFilter || teamFilter
                ? "No tasks match your current filters."
                : "No tasks available at the moment."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Team
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Person
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50">
                    {/* Type Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getTaskTypeIcon(task.type)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTaskTypeBadgeClass(task.type)}`}>
                          {task.type}
                        </span>
                      </div>
                    </td>
                    
                    {/* ID Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono font-medium text-blue-600">
                        {task.type === 'Support' ? 
                          `#${task.ticket_id?.slice(-8) || task.id.slice(-8)}` : 
                          `T${task.task_index + 1}`
                        }
                      </div>
                    </td>
                    
                    {/* Task Name Column - Clean, no project/client info */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.task_name} 
                        maxLength={20}
                        className="font-medium text-gray-900"
                      />
                    </td>
                    
                    {/* Client Column - New column with truncation */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.client_name} 
                        maxLength={12}
                        className="text-gray-900"
                      />
                    </td>
                    
                    {/* Team Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <TruncatedText 
                        text={task.team_name} 
                        maxLength={10}
                        className="text-gray-900"
                      />
                    </td>
                    
                    {/* Person Column - With truncation */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {task.assigned_employee_name ? (
                          <>
                            <div className="w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {task.assigned_employee_name.charAt(0)}
                            </div>
                            <TruncatedText 
                              text={task.assigned_employee_name} 
                              maxLength={10}
                              className="text-gray-900"
                            />
                          </>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <div className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-xs font-medium">
                              ?
                            </div>
                            <span className="text-orange-600 text-sm">
                              {task.type === 'Support' ? 'Awaiting Assignment' : 'Unassigned'}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    
                    {/* Status Column */}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(task.status)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusBadgeClass(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                    </td>
                    
                    {/* Created Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(task.created_at).toLocaleDateString()}
                    </td>
                    
                    {/* Action Column */}
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleTaskView(task)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filteredTasks.length > 0 && (
        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalTasks)} of {totalTasks} tasks
          </div>
          
          {Math.ceil(totalTasks / itemsPerPage) > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, Math.ceil(totalTasks / itemsPerPage)) }, (_, i) => {
                  const totalPages = Math.ceil(totalTasks / itemsPerPage);
                  let pageNumber;
                  
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        currentPage === pageNumber
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalTasks / itemsPerPage)))}
                disabled={currentPage === Math.ceil(totalTasks / itemsPerPage)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnifiedTasks;